import { useId, useState } from "react";
import type { ChangeEvent, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { t } from "../i18n/t";
import { cx } from "./klasy";
import { IkonaOko, IkonaOkoOff, IkonaSzukaj, IkonaUsterka } from "./ikony";
import type { KomponentIkony } from "./ikony";
import "../styles/wspolne.css";

interface WspolneProps {
  etykieta: string;
  /** Ukrywa etykietę wizualnie (zostaje dla czytników) */
  ukryjEtykiete?: boolean;
  pomoc?: ReactNode;
  /** Komunikat błędu - pole dostaje czerwoną ramkę i aria-invalid */
  blad?: string;
  className?: string;
}

function Obudowa({ id, etykieta, ukryjEtykiete, pomoc, blad, className, children }: WspolneProps & { id: string; children: ReactNode }) {
  return (
    <div className={cx("iot-pole", blad && "is-error", className)}>
      <label htmlFor={id} className={cx("iot-pole__etykieta", ukryjEtykiete && "iot-sr")}>{etykieta}</label>
      {children}
      {blad && <span className="iot-pole__blad" id={`${id}-blad`}><IkonaUsterka />{blad}</span>}
      {pomoc && !blad && <span className="iot-pole__pomoc" id={`${id}-pomoc`}>{pomoc}</span>}
    </div>
  );
}

function opisPola(id: string, pomoc?: ReactNode, blad?: string): string | undefined {
  if (blad) return `${id}-blad`;
  if (pomoc) return `${id}-pomoc`;
  return undefined;
}

export interface PoleTekstoweProps extends WspolneProps, Omit<InputHTMLAttributes<HTMLInputElement>, "className" | "onChange" | "value"> {
  wartosc: string;
  onZmien: (wartosc: string) => void;
  /** Jednostka po prawej stronie pola, np. "°C", "kg" */
  jednostka?: string;
  ikona?: KomponentIkony;
}

/** Pole tekstowe z etykietą, pomocą, błędem, opcjonalną jednostką lub ikoną. */
export function PoleTekstowe({ etykieta, ukryjEtykiete, pomoc, blad, className, wartosc, onZmien, jednostka, ikona: Ikona, type = "text", ...reszta }: PoleTekstoweProps) {
  const id = useId();
  return (
    <Obudowa id={id} etykieta={etykieta} ukryjEtykiete={ukryjEtykiete} pomoc={pomoc} blad={blad} className={className}>
      <div className="iot-pole__ramka">
        {Ikona && <Ikona className="iot-pole__ikona" />}
        <input
          id={id}
          type={type}
          className="iot-wejscie"
          value={wartosc}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onZmien(e.target.value)}
          aria-invalid={blad ? true : undefined}
          aria-describedby={opisPola(id, pomoc, blad)}
          {...reszta}
        />
        {jednostka && <span className="iot-pole__jednostka" aria-hidden="true">{jednostka}</span>}
      </div>
    </Obudowa>
  );
}

export interface PoleHaslaProps extends Omit<PoleTekstoweProps, "type" | "jednostka" | "ikona"> {
  autoComplete?: "current-password" | "new-password" | "off";
}

/** Pole hasła z przyciskiem „pokaż hasło” (stan widoczności jest lokalny - to tylko wygląd). */
export function PoleHasla({ etykieta, ukryjEtykiete, pomoc, blad, className, wartosc, onZmien, autoComplete = "current-password", ...reszta }: PoleHaslaProps) {
  const id = useId();
  const [widoczne, ustawWidoczne] = useState(false);
  return (
    <Obudowa id={id} etykieta={etykieta} ukryjEtykiete={ukryjEtykiete} pomoc={pomoc} blad={blad} className={className}>
      <div className="iot-pole__ramka">
        <input
          id={id}
          type={widoczne ? "text" : "password"}
          className="iot-wejscie"
          value={wartosc}
          autoComplete={autoComplete}
          onChange={(e) => onZmien(e.target.value)}
          aria-invalid={blad ? true : undefined}
          aria-describedby={opisPola(id, pomoc, blad)}
          {...reszta}
        />
        <button
          type="button"
          className="iot-btn-ikona iot-pole__przycisk"
          aria-label={widoczne ? t("Ukryj hasło") : t("Pokaż hasło")}
          aria-pressed={widoczne}
          onClick={() => ustawWidoczne((v) => !v)}
        >
          {widoczne ? <IkonaOkoOff /> : <IkonaOko />}
        </button>
      </div>
    </Obudowa>
  );
}

export interface PoleWielowierszoweProps extends WspolneProps, Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "className" | "onChange" | "value"> {
  wartosc: string;
  onZmien: (wartosc: string) => void;
}

export function PoleWielowierszowe({ etykieta, ukryjEtykiete, pomoc, blad, className, wartosc, onZmien, rows = 3, ...reszta }: PoleWielowierszoweProps) {
  const id = useId();
  return (
    <Obudowa id={id} etykieta={etykieta} ukryjEtykiete={ukryjEtykiete} pomoc={pomoc} blad={blad} className={className}>
      <textarea id={id} className="iot-wejscie" rows={rows} value={wartosc} onChange={(e) => onZmien(e.target.value)} aria-invalid={blad ? true : undefined} aria-describedby={opisPola(id, pomoc, blad)} {...reszta} />
    </Obudowa>
  );
}

export interface OpcjaWyboru<W extends string = string> {
  wartosc: W;
  etykieta: string;
  wylaczona?: boolean;
}

export interface PoleWyboruProps<W extends string> extends WspolneProps, Omit<SelectHTMLAttributes<HTMLSelectElement>, "className" | "onChange" | "value"> {
  wartosc: W;
  opcje: ReadonlyArray<OpcjaWyboru<W>>;
  onZmien: (wartosc: W) => void;
}

/** Lista rozwijana (natywny select - najlepszy na telefonach). */
export function PoleWyboru<W extends string>({ etykieta, ukryjEtykiete, pomoc, blad, className, wartosc, opcje, onZmien, ...reszta }: PoleWyboruProps<W>) {
  const id = useId();
  return (
    <Obudowa id={id} etykieta={etykieta} ukryjEtykiete={ukryjEtykiete} pomoc={pomoc} blad={blad} className={className}>
      <select
        id={id}
        className="iot-wejscie"
        value={wartosc}
        onChange={(e) => {
          const wybrana = opcje.find((o) => o.wartosc === e.target.value);
          if (wybrana) onZmien(wybrana.wartosc);
        }}
        aria-describedby={opisPola(id, pomoc, blad)}
        {...reszta}
      >
        {opcje.map((o) => (
          <option key={o.wartosc} value={o.wartosc} disabled={o.wylaczona}>{o.etykieta}</option>
        ))}
      </select>
    </Obudowa>
  );
}

export interface PoleSzukaniaProps {
  etykieta: string;
  wartosc: string;
  onZmien: (wartosc: string) => void;
  podpowiedz?: string;
  className?: string;
}

/** Zaokrąglone pole szukania z lupą (etykieta tylko dla czytników). */
export function PoleSzukania({ etykieta, wartosc, onZmien, podpowiedz, className }: PoleSzukaniaProps) {
  const id = useId();
  return (
    <div className={cx("iot-szukaj", className)}>
      <label htmlFor={id} className="iot-sr">{etykieta}</label>
      <IkonaSzukaj />
      <input id={id} type="search" className="iot-wejscie" value={wartosc} placeholder={podpowiedz} onChange={(e) => onZmien(e.target.value)} />
    </div>
  );
}

export interface PrzelacznikProps {
  etykieta: ReactNode;
  pomoc?: ReactNode;
  wlaczony: boolean;
  onZmien: (wlaczony: boolean) => void;
  wylaczony?: boolean;
  /** Etykieta po lewej, przełącznik po prawej (listy ustawień) */
  odwrotny?: boolean;
  /** Tylko dla czytników - gdy etykieta wizualna jest gdzie indziej */
  etykietaDostepna?: string;
  ikona?: KomponentIkony;
  className?: string;
}

/** Przełącznik włącz/wyłącz (role="switch"). */
export function Przelacznik({ etykieta, pomoc, wlaczony, onZmien, wylaczony, odwrotny, etykietaDostepna, ikona: Ikona, className }: PrzelacznikProps) {
  return (
    <label className={cx("iot-przelacznik", odwrotny && "iot-przelacznik--odwrotny", className)}>
      <input
        type="checkbox"
        role="switch"
        className="iot-przelacznik__input"
        checked={wlaczony}
        disabled={wylaczony}
        aria-label={etykietaDostepna}
        onChange={(e) => onZmien(e.target.checked)}
      />
      {Ikona && <Ikona />}
      <span className="iot-przelacznik__opis">
        {etykieta}
        {pomoc && <small className="iot-przelacznik__pomoc">{pomoc}</small>}
      </span>
    </label>
  );
}

export interface PoleZaznaczeniaProps {
  etykieta: ReactNode;
  pomoc?: ReactNode;
  zaznaczone: boolean;
  onZmien: (zaznaczone: boolean) => void;
  wylaczone?: boolean;
  /** Skreśla tekst po zaznaczeniu (lista zakupów) */
  przekreslaj?: boolean;
  className?: string;
}

export function PoleZaznaczenia({ etykieta, pomoc, zaznaczone, onZmien, wylaczone, przekreslaj, className }: PoleZaznaczeniaProps) {
  return (
    <label className={cx("iot-wybor", przekreslaj && "iot-wybor--przekreslany", className)}>
      <input type="checkbox" className="iot-wybor__input" checked={zaznaczone} disabled={wylaczone} onChange={(e) => onZmien(e.target.checked)} />
      <span>
        <span className="iot-wybor__tekst">{etykieta}</span>
        {pomoc && <small className="iot-wybor__pomoc">{pomoc}</small>}
      </span>
    </label>
  );
}

// Karty wyboru, krokomierz, pole na zdjęcia - w ./PolaDodatkowe (limit 300 linii), import dalej przez ./Pola.
export * from "./PolaDodatkowe";
